import { appUserLocated } from '@actions/app.action';
import {
  stationGetActiveStationsWithMarketsSuccess,
  stationGetActiveStationsWithConnectionMarketsSuccess,
  updateNearestStation,
} from '@actions/station.action';
import { Action, createReducer, on } from '@ngrx/store';
import {
  Station,
  StationWithDistance,
} from 'src/app/models/responses/station-response.model';
import { getDistance } from 'geolib';
import { stationLatLongToNumbers } from '@utils/data-utils';
import { NullableString } from '@models/app.models';

export interface State {
  nearestStation: Station | null;
  nearestStationCode: NullableString;
  stations: Station[] | null;
  stationsWithConnections: Station[] | null;
  userLocation: { lat: number; lon: number, byPrompt: boolean } | null;
}

export const INITIAL_STATE: State = {
  nearestStation: null,
  nearestStationCode: null,
  stations: null,
  stationsWithConnections: null,
  userLocation: null,
};

const reducer = createReducer(
  INITIAL_STATE,

  on(
    stationGetActiveStationsWithMarketsSuccess,
    (state, action): State => ({
      ...state,
      stations: action.payload,
    }),
  ),

  on(appUserLocated, (state, action): State => {
    return {
      ...state,
      userLocation: action,
    };
  }),

  on(
    appUserLocated,
    stationGetActiveStationsWithMarketsSuccess,
    (state): State => {
      if (!state.stations || !state.userLocation) {
        return {
          ...state,
        };
      }

      const stationsWithDistance = state.stations.map((station) => {
        const latStr = station.locationDetails.coordinates.latitude;
        const lonStr = station.locationDetails.coordinates.longitude;
        if (!latStr || !lonStr || !state.userLocation?.lon || !state.userLocation?.lat) {
          return {
            ...station,
            distance: Infinity,
          } as StationWithDistance;
        }

        const distance = getDistance(
          state.userLocation,
          stationLatLongToNumbers(latStr, lonStr),
        );

        return {
          ...station,
          distance,
        } as StationWithDistance;
      });

      stationsWithDistance.sort((stationA, stationB) => {
        return stationA.distance - stationB.distance;
      });

      return {
        ...state,
        nearestStation: stationsWithDistance[0] ?? null,
      };
    },
  ),

  on(
    stationGetActiveStationsWithConnectionMarketsSuccess,
    (state, action): State => ({
      ...state,
      stationsWithConnections: action.payload
        .filter((s) => s.validMarkets?.some((m) => !m.isDohop))
        ?.sort((a, b) => {
          return a.fullName > b.fullName ? 1 : a.fullName < b.fullName ? -1 : 0;
        })
        .concat(
          action.payload
            .filter((s) => !s.validMarkets?.some((m) => !m.isDohop))
            ?.sort((a, b) => {
              return a.fullName > b.fullName
                ? 1
                : a.fullName < b.fullName
                  ? -1
                  : 0;
            }),
        ),
    }),
  ),

  on(updateNearestStation, (state, { stationCode }): State => {
    return {
      ...state,
      nearestStationCode: stationCode,
    };
  }),

  on(
    updateNearestStation,
    stationGetActiveStationsWithMarketsSuccess,
    (state): State => {
      if (!state.stations || !state.nearestStationCode) {
        return {
          ...state,
        };
      }
      return {
        ...state,
        nearestStation:
          state.stations.find(
            (station) => station.stationCode === state.nearestStationCode,
          ) ?? null,
      };
    },
  ),
);

export const stationReducer = (state: State, action: Action): State =>
  reducer(state, action);
